import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { Badge } from "@/components/Badge";
import { Card } from "@/components/ui/card";
import { Loader2, CalendarHeart, ChevronRight } from "lucide-react";

const CAT_COLOR = { national: "blue", religious: "orange", regional: "green", awareness: "blue", marketing: "grey" };

export function UpcomingFestivals({ days = 45, showCoverage = false }) {
  const navigate = useNavigate();
  const { data = [], isLoading } = useQuery({ queryKey: ["festivals", days], queryFn: async () => (await api.get(`/festivals?days=${days}`)).data });

  return (
    <Card className="rounded-lg border-zinc-200 shadow-sm" data-testid="upcoming-festivals">
      <div className="flex items-center gap-2 border-b border-zinc-100 px-5 py-4">
        <CalendarHeart className="h-4 w-4 text-rose-500" />
        <h3 className="font-heading text-base font-semibold">Upcoming Special Days & Festivals</h3>
        <Badge color="orange">{data.length}</Badge>
      </div>
      {isLoading ? (
        <div className="flex h-24 items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-zinc-400" /></div>
      ) : data.length === 0 ? (
        <div className="py-8 text-center text-sm text-zinc-400">No special days in the next {days} days</div>
      ) : (
        <div className="divide-y divide-zinc-50">
          {data.map((f) => (
            <button key={f.id} onClick={() => navigate(`/special-days?festival=${f.id}`)} data-testid={`festival-${f.id}`} className="flex w-full items-center gap-3 px-5 py-3 text-left hover:bg-zinc-50">
              <span className="text-xl">{f.emoji}</span>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="truncate text-sm font-semibold text-zinc-900">{f.name}</span>
                  <Badge color={CAT_COLOR[f.category] || "grey"}>{f.category}</Badge>
                </div>
                <div className="text-xs text-zinc-500">{fmtDate(f.date)} · {f.region}
                  {showCoverage && f.total_clients > 0 && <span className={f.planned_count < f.total_clients ? "ml-2 text-amber-600" : "ml-2 text-emerald-600"}>· {f.planned_count}/{f.total_clients} clients planned</span>}
                </div>
              </div>
              <div className="text-right">
                <div className={`metric-value text-sm font-semibold ${f.days_remaining <= 7 ? "text-red-600" : f.days_remaining <= 21 ? "text-amber-600" : "text-zinc-700"}`}>{f.days_remaining}d</div>
                <div className="text-[10px] text-zinc-400">remaining</div>
              </div>
              <ChevronRight className="h-4 w-4 text-zinc-300" />
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}
